import GoogleIcon from "@/assets/icons/google.svg";
import MicrosoftIcon from "@/assets/icons/microsoft.svg";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";
import { ROUTES } from "@/routes_new";
import AllEarsTitle from "@components/AllEarsTitle";
import SignupImage from "@components/SignupImage";
import { Button } from "@components/ui/button";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import { Input } from "@components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@components/ui/input-group";
import Logo from "@components/ui/logo";
import { Separator } from "@components/ui/separator";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { EyeClosedIcon, EyeIcon } from "lucide-react";
import { stringifyUrl } from "@/utils";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { authorizeUser } from "@/api/endpoints/user";
import { useLoginFormSchema } from "../shared/useLoginFormSchema";
import useSearchParams from "@/hooks/useSearchParams";
import { applyServerErrors } from "@/utils/forms";

export default function LoginView() {
  const { t } = useLingui();

  // URL state
  const [getParams] = useSearchParams<{ error?: string; next?: string }>();
  const ssoError = getParams?.error;
  const nextParam = getParams?.next;

  // Local state
  const formSchema = useLoginFormSchema();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const { mutateAsync: mutateLogin } = useMutation({
    mutationFn: authorizeUser,
    onSuccess: async (res) => {
      if (res.ok) {
        window.location.href = nextParam || ROUTES.APP_HOME;
        return;
      }

      if (res.status === 401 && res.data) {
        applyServerErrors(res.data, form);
        return;
      }

      throw new Error();
    },
    onError: () => {
      toast.error(t`Hmm... Någonting gick snett`);
    },
  });

  const [isShowingPassword, setIsShowingPassword] = useState<boolean>(false);
  const showSpinner = useDelayedLoading(form.formState.isSubmitting);

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    return mutateLogin({
      username: values.username,
      password: values.password,
    });
  };

  return (
    <>
      <AllEarsTitle title={t`Logga in`} />

      <div className="md:h-svh grid md:grid-cols-2">
        <div className="p-7 md:p-14 flex flex-col gap-[15svh] overflow-y-auto h-full">
          <Logo width={80} className="self-start" />

          <div className="w-full space-y-6 max-w-lg mx-auto">
            <div className="flex flex-col items-center text-balance text-center gap-1.5">
              <h1 className="text-2xl font-semibold">
                <Trans>Logga in</Trans>
              </h1>
              <p className="text-sm text-muted-foreground">
                <Trans>Upptäck världens talade media</Trans>
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <Button
                data-testid="login-sso-google"
                type="button"
                variant="outline"
                asChild
              >
                <a
                  href={stringifyUrl({
                    url: ROUTES.LOGIN_SSO_GOOGLE,
                    query: nextParam ? { next: nextParam } : undefined,
                  })}
                >
                  <GoogleIcon />
                  <Trans>Fortsätt med Google</Trans>
                </a>
              </Button>

              <Button
                data-testid="login-sso-azure"
                type="button"
                variant="outline"
                asChild
              >
                <a
                  href={stringifyUrl({
                    url: ROUTES.LOGIN_SSO_AZURE,
                    query: nextParam ? { next: nextParam } : undefined,
                  })}
                >
                  <MicrosoftIcon />
                  <Trans>Fortsätt med Microsoft</Trans>
                </a>
              </Button>
            </div>

            <div className="flex gap-2 items-center">
              <Separator className="flex-1" />
              <p className="text-xs text-muted-foreground text-center uppercase tracking-wide">
                <Trans>Eller</Trans>
              </p>
              <Separator className="flex-1" />
            </div>

            <form
              className="flex flex-col gap-6"
              onSubmit={form.handleSubmit(handleSubmit)}
              data-testid="login-form"
            >
              <FieldGroup className="gap-5">
                <Controller
                  control={form.control}
                  name="username"
                  render={({ field, fieldState }) => {
                    const id = "email";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>E-postadress för arbetet</Trans>
                        </FieldLabel>

                        <Input
                          {...field}
                          id={id}
                          data-testid="login-username"
                          autoComplete="work email"
                          aria-invalid={fieldState.invalid}
                          placeholder={t`namn@foretag.com`}
                        />

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                <Controller
                  control={form.control}
                  name="password"
                  render={({ field, fieldState }) => {
                    const id = "password";

                    return (
                      <Field data-invalid={fieldState.invalid}>
                        <FieldLabel htmlFor={id}>
                          <Trans>Lösenord</Trans>

                          <Link
                            data-testid="login-forgot-password"
                            className="link ml-auto font-normal underline"
                            to={ROUTES.FORGOT_PASSWORD}
                          >
                            <Trans>Glömt ditt lösenord?</Trans>
                          </Link>
                        </FieldLabel>

                        <InputGroup>
                          <InputGroupInput
                            {...field}
                            id={id}
                            autoComplete="password"
                            data-testid="login-password"
                            placeholder="•••••••••••••"
                            aria-invalid={fieldState.invalid}
                            type={isShowingPassword ? "text" : "password"}
                          />

                          <InputGroupAddon align="inline-end">
                            <InputGroupButton
                              type="button"
                              onClick={() => {
                                setIsShowingPassword((prev) => !prev);
                              }}
                            >
                              {isShowingPassword ? (
                                <EyeClosedIcon />
                              ) : (
                                <EyeIcon />
                              )}
                            </InputGroupButton>
                          </InputGroupAddon>
                        </InputGroup>

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                {ssoError && (
                  <FieldError data-testid="oauth-login-error">
                    {ssoError === "personal-email" ? (
                      <Trans>
                        Ange din e-post för jobbet, inte Gmail, Hotmail eller
                        andra privata e-postadresser.
                      </Trans>
                    ) : (
                      <Trans>
                        Användaren hittades inte. Kontrollera att du loggar in
                        med rätt uppgifter eller{" "}
                        <a className="link" href="mailto:support@allears.ai">
                          kontakta vår support
                        </a>{" "}
                        om du behöver hjälp.
                      </Trans>
                    )}
                  </FieldError>
                )}
              </FieldGroup>

              <Button
                data-testid="login-submit"
                disabled={showSpinner.shouldRender}
                type="submit"
              >
                <Trans>Logga in</Trans>
              </Button>

              <p className="text-center text-sm text-muted-foreground text-balance">
                <Trans>
                  Har du inget konto?{" "}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={ROUTES.SIGNUP}
                    className="underline hover:text-foreground transition"
                  >
                    Skapa ett här
                  </a>
                  .
                </Trans>
              </p>
            </form>
          </div>
        </div>

        <SignupImage />
      </div>
    </>
  );
}
